/**
 * Study Dashboard Page
 *
 * Personalized quiz recommendations to solve:
 * - Paradox of choice (300+ datasets is overwhelming)
 * - Decision fatigue ("What should I study today?")
 * - Bringing users back daily (changing recommendations)
 * - Celebrating progress ("Almost there!" section)
 *
 * Philosophy (from STATS_PHILOSOPHY.md):
 * - NO competition: Only "you vs past you"
 * - Mistakes are learning: Frame low scores positively
 * - Review is essential: Make reviews inviting, not mandatory
 * - Gentle motivation: Encouraging, not pushy
 * - Effort over perfection: Celebrate attempts
 *
 * UX Design (from UX_DESIGN.md):
 * - Modern, playful, professional (Duolingo + Linear aesthetic)
 * - Glass-morphic cards matching PersonalStatsPage
 * - Vibrant category colors
 * - Generous whitespace
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QuickActionCard } from "@/components/study/QuickActionCard";
import { QuizCard } from "@/components/quiz/QuizCard";
import { Badge } from "@/components/ui/badge";
import { db } from "@/db/database";
import {
  getRecommendations,
  getRecommendationStats,
  getRandomQuiz,
  type Recommendation,
} from "@/utils/recommendationEngine";
import { Dice5, Shuffle, Search, Sparkles } from "lucide-react";

export function StudyDashboardPage() {
  const navigate = useNavigate();
  const [isRandomizing, setIsRandomizing] = useState(false);

  // Load data
  const knowledgeStates = useLiveQuery(() => db.knowledge_state.toArray());
  const allQuizzes = useLiveQuery(() => db.quiz_configs.toArray());

  // Calculate recommendations
  const recommendations = useMemo(() => {
    if (!knowledgeStates || !allQuizzes) return [];
    return getRecommendations(knowledgeStates, allQuizzes, 8);
  }, [knowledgeStates, allQuizzes]);

  // Calculate stats for quick action cards
  const stats = useMemo(() => {
    if (!knowledgeStates || !allQuizzes) {
      return {
        dueForReview: 0,
        needsWork: 0,
        almostThere: 0,
        unexplored: 0,
      };
    }
    return getRecommendationStats(knowledgeStates, allQuizzes);
  }, [knowledgeStates, allQuizzes]);

  // Handle "Surprise Me!" button
  const handleRandomQuiz = async () => {
    if (!allQuizzes || allQuizzes.length === 0) return;

    setIsRandomizing(true);

    // Add a small delay for dramatic effect
    await new Promise((resolve) => setTimeout(resolve, 300));

    const randomRec = getRandomQuiz(allQuizzes, knowledgeStates);
    if (randomRec) {
      handlePlayQuiz(randomRec);
    }

    setIsRandomizing(false);
  };

  // Handle playing a quiz from recommendation
  const handlePlayQuiz = (rec: Recommendation) => {
    navigate(`/quiz/play?signature=${rec.quiz.signature}`);
  };

  // Check for empty state
  const hasQuizzes = allQuizzes && allQuizzes.length > 0;
  const hasRecommendations = recommendations.length > 0;
  const isLoading = !knowledgeStates || !allQuizzes;

  return (
    <MainLayout
      title="Study Dashboard"
      subtitle="Your personalized learning recommendations"
    >
      <div className="container mx-auto p-4 md:p-6 max-w-7xl space-y-8">
        {/* Loading State */}
        {isLoading && (
          <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Loading recommendations...</p>
            </CardContent>
          </Card>
        )}

        {/* Empty State - No Quizzes */}
        {!isLoading && !hasQuizzes && (
          <Card className="border-dashed border-2 border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
            <CardContent className="p-12 text-center space-y-6">
              <div className="text-6xl">🗺️</div>
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">Ready to Start Learning?</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  You haven't created any quizzes yet. Browse our catalogue of geographic
                  datasets or jump into a featured quiz to get started!
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  onClick={() => navigate("/geojson/browse")}
                >
                  <Search className="w-5 h-5 mr-2" />
                  Browse Datasets
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/")}
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  See Featured Quizzes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        {!isLoading && hasQuizzes && (
          <>
            {/* Quick Stats Grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <QuickActionCard
                emoji="🔄"
                title="Due for Review"
                count={stats.dueForReview}
                subtitle="Keep your knowledge fresh"
              />
              <QuickActionCard
                emoji="💪"
                title="Needs Work"
                count={stats.needsWork}
                subtitle="Let's practice together"
              />
              <QuickActionCard
                emoji="🎯"
                title="Almost There"
                count={stats.almostThere}
                subtitle="You're so close!"
              />
              <QuickActionCard
                emoji="✨"
                title="Unexplored"
                count={stats.unexplored}
                subtitle="Brand new datasets"
              />
            </div>

            {/* Randomizer Section - BIG CTA */}
            <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center space-y-4">
                <Dice5 className="w-16 h-16 mx-auto text-primary animate-pulse" />
                <h2 className="text-2xl md:text-3xl font-bold">Feeling Lucky?</h2>
                <p className="text-muted-foreground text-lg max-w-md mx-auto">
                  Let us pick a random quiz for you. Sometimes the best learning happens
                  when you least expect it!
                </p>
                <Button
                  size="lg"
                  onClick={handleRandomQuiz}
                  disabled={isRandomizing || !hasQuizzes}
                  className="text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  {isRandomizing ? (
                    <>
                      <Dice5 className="w-5 h-5 mr-2 animate-spin" />
                      Picking...
                    </>
                  ) : (
                    <>
                      <Shuffle className="w-5 h-5 mr-2" />
                      Surprise Me!
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Recommendations Section */}
            {hasRecommendations ? (
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl md:text-3xl font-bold">Recommended for You</h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Personalized quiz suggestions based on your learning history. Each visit
                    brings fresh recommendations!
                  </p>
                </div>

                {/* Recommendations Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {recommendations.map((rec, idx) => (
                    <div key={`${rec.quiz.signature}-${idx}`} className="space-y-2">
                      {/* Reason Badge - Above Card */}
                      <Badge
                        variant="secondary"
                        className={getBadgeClassName(rec.reason)}
                      >
                        {getBadgeEmoji(rec.reason)} {rec.reasonLabel}
                      </Badge>

                      {/* Quiz Card */}
                      <QuizCard
                        quiz={rec.quiz}
                        showActions={false}
                      />
                    </div>
                  ))}
                </div>

                {/* Browse All Link */}
                <div className="text-center pt-8">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate("/quiz/my-quizzes")}
                  >
                    View All My Quizzes
                  </Button>
                </div>
              </div>
            ) : (
              /* No Recommendations - Gentle Nudge */
              <Card className="border border-border/70 bg-white/80 shadow-lg shadow-primary/10 backdrop-blur dark:bg-secondary/70">
                <CardContent className="p-12 text-center space-y-4">
                  <div className="text-5xl">🎯</div>
                  <h3 className="text-xl font-semibold">No Recommendations Yet</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Complete some quizzes to start getting personalized recommendations!
                    Your first quiz is just a click away.
                  </p>
                  <Button
                    size="lg"
                    onClick={() => navigate("/quiz/my-quizzes")}
                  >
                    View My Quizzes
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}

/**
 * Get badge emoji for a recommendation reason
 */
function getBadgeEmoji(reason: Recommendation["reason"]): string {
  switch (reason) {
    case "due":
      return "🔄";
    case "work":
      return "💪";
    case "almost":
      return "🎯";
    case "new":
      return "✨";
    case "shuffle":
      return "🎲";
    default:
      return "📍";
  }
}

/**
 * Get badge className for a recommendation reason
 */
function getBadgeClassName(reason: Recommendation["reason"]): string {
  switch (reason) {
    case "due":
      return "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900 dark:text-orange-300";
    case "work":
      return "bg-red-100 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-300";
    case "almost":
      return "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300";
    case "new":
      return "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900 dark:text-blue-300";
    case "shuffle":
      return "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900 dark:text-purple-300";
    default:
      return "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-300";
  }
}
